(() => {
  if (customElements.get('screw-size-selector')) return;

  const selections = new Map();
  const isValid = value => value === 'M6' || value === 'M8';

  const controllers = () => [
    ...document.querySelectorAll('screw-size-selector')
  ];

  const controllerFor = form => {
    if (!form) return null;

    const source = form.querySelector(
      '[data-screw-source]'
    )?.dataset.screwSource;

    return controllers().find(
      el => el.dataset.form === (
        source || form.getAttribute('id')
      )
    );
  };

  class ScrewSizeSelector extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (!this.isConnected) return;

        if (this.ready) {
          this.bindForms();
          return;
        }

        this.ready = true;

        this.radios = [
          ...this.querySelectorAll('input[type="radio"]')
        ];

        this.error = this.querySelector('.screw-size__error');
        this.help = this.querySelector('.screw-size__help');
        this.unsure = this.querySelector('.screw-size__unsure');
        this.status = this.querySelector('[data-screw-help-status]');

        this.key = this.dataset.form + ':' + this.dataset.product;

        const saved = selections.get(this.key);

        if (isValid(saved)) {
          this.radios.forEach(radio => {
            radio.checked = radio.value === saved;
          });
        }

        this.addEventListener('change', event => {
          if (!this.radios.includes(event.target)) return;

          this.help.hidden = true;
          this.unsure.setAttribute('aria-expanded', 'false');
          this.error.hidden = true;
          this.sync();
        });

        this.unsure.addEventListener('click', () => {
          this.radios.forEach(radio => {
            radio.checked = false;
          });

          this.help.hidden = false;
          this.unsure.setAttribute('aria-expanded', 'true');

          this.sync();
          this.bindForms();

          if (
            this.help.classList.contains(
              'screw-size__help--success'
            )
          ) {
            this.status.focus();
          } else {
            this.formsShadow
              ?.querySelector('[inputmode="email"]')
              ?.focus();
          }
        });

        if (
          new URL(location.href)
            .searchParams.has('screw_size_help')
        ) {
          this.help.hidden = false;
          this.unsure.setAttribute('aria-expanded', 'true');
        }

        this.sync();
        this.bindForms();
      });
    }

    disconnectedCallback() {
      clearTimeout(this.formsTimer);
      this.formsObserver?.disconnect();
      this.formsAbort?.abort();
      this.formsShadow = null;
    }

    bindForms(attempt = 0) {
      clearTimeout(this.formsTimer);

      if (!this.isConnected) return;

      const shadow = this.querySelector(
        'shopify-forms-embed'
      )?.shadowRoot;

      if (!shadow) {
        if (attempt < 80) {
          this.formsTimer = setTimeout(
            () => this.bindForms(attempt + 1),
            250
          );
        }

        return;
      }

      if (this.formsShadow === shadow) return;

      this.formsObserver?.disconnect();
      this.formsAbort?.abort();

      this.formsShadow = shadow;
      this.formsAbort = new AbortController();

      // Shopify Forms сам виконує валідацію та надсилання.
      shadow.addEventListener(
        'submit',
        () => {
          this.submittedEmail = shadow
            .querySelector('[inputmode="email"]')
            ?.value.trim() || '';
        },
        {
          capture: true,
          signal: this.formsAbort.signal
        }
      );

      this.formsObserver = new MutationObserver(() => {
        if (
          shadow.querySelector(
            '[data-current-step="success"]'
          )
        ) {
          this.showFormsSuccess();
        }
      });

      this.formsObserver.observe(shadow, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['data-current-step']
      });
    }

    showFormsSuccess() {
      if (
        this.help.classList.contains(
          'screw-size__help--success'
        )
      ) {
        return;
      }

      this.help.classList.add('screw-size__help--success');
      this.help.hidden = false;

      this.querySelector('[data-screw-forms]').hidden = true;

      this.status.hidden = false;
      this.status.textContent = this.status.dataset.success;

      if (this.submittedEmail) {
        const [beforeEmail, ...afterEmail] =
          this.status.dataset.successEmail.split('[email]');

        const address = document.createElement('span');

        address.className = 'screw-size__email-address';
        address.textContent = this.submittedEmail;

        this.status.replaceChildren(
          beforeEmail,
          address,
          afterEmail.join('[email]')
        );
      }

      this.status.setAttribute('tabindex', '-1');
      this.unsure.setAttribute('aria-expanded', 'true');
      this.status.focus({ preventScroll: true });

      const url = new URL(location.href);

      url.searchParams.set('screw_size_help', 'form');

      history.replaceState(
        history.state,
        '',
        url.pathname + url.search + url.hash
      );
    }

    get value() {
      return this.querySelector(
        'input[type="radio"]:checked'
      )?.value || '';
    }

    sync() {
      const value = this.value;

      selections.set(this.key, value);

      const primary = document.getElementById(
        this.dataset.form
      );

      const forms = [primary];

      document.querySelectorAll(
        '[data-screw-source]'
      ).forEach(input => {
        if (
          input.dataset.screwSource !== this.dataset.form
        ) {
          return;
        }

        input.value = value;
        forms.push(input.form);
      });

      forms.filter(Boolean).forEach(form => {
        form.querySelectorAll(
          '[data-screw-checkout]'
        ).forEach(button => {
          button.hidden = !isValid(value);
        });
      });
    }

    validate() {
      this.sync();

      if (isValid(this.value)) return true;

      this.error.hidden = false;

      this.scrollIntoView({
        block: 'center',
        behavior: 'smooth'
      });

      this.radios[0].focus({ preventScroll: true });

      return false;
    }
  }

  customElements.define(
    'screw-size-selector',
    ScrewSizeSelector
  );

  window.addEventListener('submit', event => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) return;

    const controller = controllerFor(form);

    if (controller && !controller.validate()) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  window.addEventListener('click', event => {
    const button = event.target.closest?.(
      'button[type="submit"], input[type="submit"]'
    );

    const controller = controllerFor(button?.form);

    if (controller && !controller.validate()) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }, true);

  window.addEventListener('invalid', event => {
    if (
      event.target.name !== 'properties[Screw Size]'
    ) {
      return;
    }

    const controller = controllerFor(event.target.form);

    if (controller) {
      event.preventDefault();
      controller.validate();
    }
  }, true);

  document.addEventListener(
    'shopify:section:load',
    () => controllers().forEach(el => el.sync())
  );

  document.addEventListener(
    'quick-view:loaded',
    () => controllers().forEach(el => el.sync())
  );
})();